import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { Switch } from '@components/Switch';
import { Skeleton } from '@mui/material';
import { useTranslation, useLocations } from '@hooks';
import { instagramChannelActions } from '@actions';
import { CHANNEL } from '@constants/channels';
import { AccIntegrationName } from '@constants/integrations';

const Container = styled('div')({
    display: 'grid',
    gridGap: '16px 24px',
    gridTemplateAreas: '"input1" "select0"',
    gridTemplateColumns: '100%',
    '@media (max-width: 600px)': {
        gridGap: '16px',
    },
});

const FacebookForm = ({ type }) => {
    const { t } = useTranslation();
    const { control, setValue, watch } = useFormContext();
    const companies = useSelector(state => state.account?.account?.companies || []);
    const instagramData = useSelector(state => state.instagramChannel.data);
    const instagramLocationsLoading = useSelector(state => state.instagramChannel.loading);
    const channels = useSelector(state => state.channels.data);

    const dispatch = useDispatch();
    const { getSelectLocationsOptions } = useLocations();

    const isInstagramModal = type === CHANNEL.INSTAGRAM;

    const locationId = watch('locationId');

    useEffect(() => {
        if (isInstagramModal) {
            dispatch(instagramChannelActions.getInstagramLocations());
        }
    }, []);

    const hasFbOrIgChannel = channelIds => {
        const connectedChannels = channelIds.map(id => channels.find(c => c.id === id)).filter(Boolean);
        return connectedChannels.some(c => c.type === type);
    };

    const facebookLocations = useMemo(() => {
        const filterCb = item =>
            item.profiles.some(i => i.type === AccIntegrationName.Facebook) &&
            (locationId === item.companyId || !hasFbOrIgChannel(item.channels));
        return getSelectLocationsOptions(filterCb);
    }, [companies, channels]);

    const instagramLocations = useMemo(() => {
        const filterCb = item =>
            instagramData.some(i => i.id === item.companyId) &&
            (locationId === item.companyId || !hasFbOrIgChannel(item.channels));
        return getSelectLocationsOptions(filterCb);
    }, [instagramData, companies, channels]);

    const companyOptions = isInstagramModal ? instagramLocations : facebookLocations;

    return (
        <>
            {!instagramLocationsLoading && (
                <Container>
                    <Controller
                        control={control}
                        name="name"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('SettingsChannels.channelName')}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                                sx={{ gridArea: 'input1' }}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="locationId"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Select
                                {...field}
                                ref={ref}
                                onChange={({ target: { value } }) => {
                                    setValue('locationId', value, { shouldValidate: true, shouldDirty: true });
                                }}
                                options={companyOptions}
                                required
                                label={t('common.location')}
                                fullWidth
                                style={{ gridArea: 'select0' }}
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                groupBased
                            />
                        )}
                    />
                    {isInstagramModal && (
                        <Controller
                            control={control}
                            name="saveInstagramReplies"
                            render={({ field, ref }) => (
                                <Switch
                                    {...field}
                                    ref={ref}
                                    checked={field.value}
                                    handleChange={event => {
                                        setValue('saveInstagramReplies', event.target.checked, {
                                            shouldDirty: true,
                                        });
                                    }}
                                    label={t('SettingsChannels.saveInstagramReplies')}
                                />
                            )}
                        />
                    )}
                </Container>
            )}
            {instagramLocationsLoading && (
                <div style={{ display: 'grid', rowGap: '32px' }}>
                    <Skeleton variant="rounded" width="90%" height={40} />
                    <Skeleton variant="rounded" width="90%" height={40} />
                    <Skeleton variant="rounded" width="50%" height={40} />
                </div>
            )}
        </>
    );
};

export default FacebookForm;
